import React, { useState, useEffect } from 'react';
import { formatCurrency, PAYMENT_TYPE_LABELS, calcItemTotal } from '../utils/checkoutCalculations';
import { useSellerTaxStore, cartTaxBreakdown } from '../../taxes/sellerTax';
import { receiptService } from '../../../_services/receipt.service';
import toast from '../../shared/Toast';

/**
 * Animated SVG checkmark - charcoal circle with check.
 */
const SuccessCheckmark = () => (
  <div className="co-checkmark-wrap">
    <svg className="co-checkmark" width="64" height="64" viewBox="0 0 64 64">
      <circle
        className="co-checkmark-circle"
        cx="32" cy="32" r="28"
        fill="none"
        stroke="#29282D"
        strokeWidth="2.5"
      />
      <polyline
        className="co-checkmark-check"
        points="20,33 28,41 44,24"
        fill="none"
        stroke="#29282D"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/**
 * CompleteScreen
 * Clean success screen with payment breakdown, receipt, and rebook.
 */
const CompleteScreen = ({ state, onDone, onRebook, currencySymbol, dispatch }) => {
  // This screen is shared by the diary checkout (state.lineItems, originalPrice
  // + per-item discounts) and the POS (state.items, price + a cart discount).
  // Read whichever is present so the "includes £x tax" line shows for both.
  const taxItems = state.lineItems || state.items || [];
  // Seller for the tax rows: a line's staffId, else the booking's stylist, else
  // the POS "Selling as" staff, else the logged-in user (audit F8 — cart lines
  // added before a seller was set carried no staffId and the row vanished).
  const taxSellerId = (taxItems.find((i) => i.staffId) || {}).staffId || state.booking?.hairdresserId || state.sellerUserId || localStorage.getItem('userId') || null;
  const sellerTaxStore = useSellerTaxStore(taxSellerId);
  const taxPriceOf = (i) => (i.originalPrice !== undefined ? calcItemTotal(i) : (Number(i.price) || 0) * (Number(i.quantity) || 1));
  const taxItemsSubtotal = taxItems.reduce((n, i) => n + taxPriceOf(i), 0);
  const taxCartDiscount = state.lineItems
    ? Math.max(0, (state.discountTotal || 0) - taxItems.reduce((n, i) => n + (calcItemTotal({ ...i, discount: null }) - calcItemTotal(i)), 0))
    : (state.discountAmount || 0);
  const taxDiscountFactor = taxItemsSubtotal > 0 ? Math.max(0, (taxItemsSubtotal - taxCartDiscount) / taxItemsSubtotal) : 1;
  const taxLines = cartTaxBreakdown(sellerTaxStore, taxItems, taxDiscountFactor, taxPriceOf);
  const [showContent, setShowContent] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [showReceiptInput, setShowReceiptInput] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const fmt = (amount) => formatCurrency(amount, currencySymbol);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const clientName = state.client?.name || 'Walk-in';
  const clientEmail = state.client?.email;
  const hasEmail = clientEmail && !clientEmail.includes('@beuapp.com');
  const totalPaidThisSession = state.transactions.reduce((sum, t) => sum + t.amount, 0);
  const bookingId = state.bookingId;

  // Pre-fill email when opening receipt input
  const handleOpenReceipt = () => {
    setReceiptEmail(hasEmail ? clientEmail : '');
    setShowReceiptInput(true);
  };

  const handleSendReceipt = async () => {
    const email = receiptEmail.trim();
    if (!email || sendingReceipt || receiptSent) return;
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSendingReceipt(true);
    try {
      if (bookingId) {
        await receiptService.sendReceiptEmail({ bookingId, email });
      }
      setReceiptSent(true);
      setShowReceiptInput(false);
      toast.success(`Receipt sent to ${email}`);
    } catch {
      toast.error('Failed to send receipt');
    }
    setSendingReceipt(false);
  };

  return (
    <div className="co-complete">
      {/* Hero */}
      <div className="co-complete-hero">
        <SuccessCheckmark />
        <div className={`co-complete-info ${showContent ? 'co-complete-info--visible' : ''}`}>
          <p className="co-complete-amount">{fmt(totalPaidThisSession)}</p>
          <p className="co-complete-client">{clientName}</p>
        </div>
      </div>

      {/* Breakdown */}
      <div className={`co-complete-breakdown ${showContent ? 'co-complete-breakdown--visible' : ''}`}>
        {state.transactions.map((t, i) => (
          <div className="co-complete-row" key={i}>
            <span>{t.label || PAYMENT_TYPE_LABELS[t.paymentType] || 'Payment'}</span>
            <span>{fmt(t.amount)}</span>
          </div>
        ))}

        {taxLines && taxLines.rows.map((r) => (
          <div key={r.label} className="co-complete-breakdown-row" style={{ color: '#8e8e93' }}>
            <span>Includes {r.label}</span>
            <span>{fmt(r.amount)}</span>
          </div>
        ))}
        {state.tipAmount > 0 && (
          <div className="co-complete-row co-complete-row--tip">
            <span>Tip</span>
            <span>{fmt(state.tipAmount)}</span>
          </div>
        )}

        {(state.discountTotal || state.discountAmount || 0) > 0 && (
          <div className="co-complete-row co-complete-row--discount">
            <span>Discount</span>
            <span>-{fmt(state.discountTotal || state.discountAmount)}</span>
          </div>
        )}

        {state.staffBreakdown && state.staffBreakdown.length > 1 && (
          <>
            <div className="co-complete-divider" />
            {state.staffBreakdown.map(staff => (
              <div className="co-complete-row" key={staff.staffId}>
                <span>{staff.staffName}</span>
                <span>{fmt(staff.subtotal)}{staff.tipShare > 0 ? ` + ${fmt(staff.tipShare)} tip` : ''}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Actions */}
      <div className={`co-complete-actions ${showContent ? 'co-complete-actions--visible' : ''}`}>
        {/* Receipt */}
        {receiptSent ? (
          <div className="co-complete-receipt co-complete-receipt--sent">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Receipt sent</span>
          </div>
        ) : showReceiptInput ? (
          <div className="co-complete-receipt-form">
            <input
              className="co-complete-receipt-input"
              type="email"
              placeholder="Email address"
              value={receiptEmail}
              onChange={e => setReceiptEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSendReceipt(); }}
              autoFocus
            />
            <button
              className="co-complete-receipt-send"
              onClick={handleSendReceipt}
              disabled={sendingReceipt || !receiptEmail.trim()}
            >
              {sendingReceipt ? (
                <div className="co-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              )}
            </button>
          </div>
        ) : (
          <button className="co-complete-receipt" onClick={handleOpenReceipt}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <span>Send receipt</span>
          </button>
        )}

        {/* Primary action */}
        {onRebook ? (
          <button className="co-btn co-btn--primary" onClick={onRebook}>
            Book next appointment
          </button>
        ) : (
          <button className="co-btn co-btn--primary" onClick={onDone}>
            Done
          </button>
        )}

        {/* Secondary */}
        {onRebook && (
          <button className="co-complete-done-link" onClick={onDone}>
            Done
          </button>
        )}
      </div>
    </div>
  );
};

export default CompleteScreen;
